import {
  Box,
  IconButton,
  Slider,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Area } from "react-easy-crop";
import Cropper from "react-easy-crop";

import { useDeleteActorProfilePictureMutation } from "Actors/Api/Mutations/useDeleteActorProfilePictureMutation";
import { useUploadActorProfilePictureMutation } from "Actors/Api/Mutations/useUploadActorProfilePictureMutation";
import { Radius } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { UserAvatar } from "Shared/Avatar/UserAvatar";
import { BlModal } from "Shared/Dialogs/BlModal";
import { DeleteIcon, UploadToCloudIcon } from "Shared/Icons";

interface ProfilePictureUploadProps {
  actorID?: string;
  userID?: string;
  profilePictureUrl?: string | null;
  firstName?: string;
  lastName?: string;
  pendingFile?: File | null;
  onPendingFileChange?: (file: File | null) => void;
}

const FieldRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDisabled",
})<{ isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px 8px 8px;
  border-radius: ${Radius.M};
  cursor: ${({ isDisabled }) => (isDisabled ? "default" : "pointer")};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.fieldBackground};
  min-height: 54px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ isDisabled }) =>
      isDisabled ? "transparent" : "rgba(255, 255, 255, 0.4)"};
  }
`;

const ProfileImage = styled("img")`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  flex-shrink: 0;
`;

async function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });
}

async function getCroppedImage(
  imageSrc: string,
  pixelCrop: Area,
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(
          new File([blob], "profile-picture.jpg", { type: "image/jpeg" }),
        );
      },
      "image/jpeg",
      0.95,
    );
  });
}

export const ProfilePictureUpload = ({
  actorID,
  userID,
  profilePictureUrl,
  firstName = "",
  lastName = "",
  pendingFile,
  onPendingFileChange,
}: ProfilePictureUploadProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);

  const isEditMode = !!actorID && !!userID;
  const uploadMutation = useUploadActorProfilePictureMutation();
  const deleteMutation = useDeleteActorProfilePictureMutation();

  const pendingPreviewUrl = useMemo(() => {
    if (!pendingFile) return null;
    return URL.createObjectURL(pendingFile);
  }, [pendingFile]);

  useEffect(() => {
    return () => {
      if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
    };
  }, [pendingPreviewUrl]);

  const displayUrl = pendingPreviewUrl ?? profilePictureUrl;

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedPixels: Area) => {
      setCroppedAreaPixels(croppedPixels);
    },
    [],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setIsCropOpen(true);
    });
    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const handleRowClick = () => {
    if (!isLoading) fileInputRef.current?.click();
  };

  const handleCropConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      const croppedFile = await getCroppedImage(imageSrc, croppedAreaPixels);
      setIsCropOpen(false);
      setImageSrc(null);

      if (isEditMode) {
        await uploadMutation.mutateAsync({ userID, file: croppedFile });
      } else {
        onPendingFileChange?.(croppedFile);
      }
    } catch {
      // error handled by mutation (auto-toast via processResponse)
    }
  };

  const handleCropCancel = () => {
    setIsCropOpen(false);
    setImageSrc(null);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditMode) {
      onPendingFileChange?.(null);
      return;
    }
    try {
      await deleteMutation.mutateAsync(userID);
    } catch {
      // error handled by mutation (auto-toast via processResponse)
    }
  };

  const hasPicture = !!displayUrl;
  const isLoading = uploadMutation.isPending || deleteMutation.isPending;

  const filenameLabel = pendingFile
    ? pendingFile.name
    : profilePictureUrl
      ? (profilePictureUrl.split("/").pop() ?? profilePictureUrl)
      : null;

  return (
    <>
      <FieldRow
        onClick={handleRowClick}
        isDisabled={isLoading}
        sx={{ opacity: isLoading ? 0.6 : 1 }}
      >
        {displayUrl ? (
          <ProfileImage src={displayUrl} alt={`${firstName} ${lastName}`} />
        ) : (
          <UserAvatar firstName={firstName} lastName={lastName} size={32} />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: filenameLabel
                ? theme.palette.text.primary
                : theme.colors.textTertiary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {filenameLabel ??
              (hasPicture
                ? t(Resources.Actors.ProfilePicture.ChangePhoto)
                : t(Resources.Actors.ProfilePicture.UploadPhoto))}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {hasPicture ? (
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={isLoading}
              sx={{ color: "error.main", p: 0.5 }}
            >
              <DeleteIcon width={14} height={14} color="currentColor" />
            </IconButton>
          ) : (
            <Box
              sx={{
                color: theme.colors.textTertiary,
                display: "flex",
                alignItems: "center",
              }}
            >
              <UploadToCloudIcon width={20} height={20} color="currentColor" />
            </Box>
          )}
        </Box>
      </FieldRow>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <BlModal
        isOpen={isCropOpen}
        title={t(Resources.Actors.ProfilePicture.CropTitle)}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
        cancelButtonText={t(Resources.Common.Cancel)}
        confirmButtonText={t(Resources.Common.Save)}
        maxWidth="500px"
      >
        <Box
          sx={(theme) => ({
            position: "relative",
            width: "100%",
            height: 360,
            bgcolor: theme.colors.backgroundScrim,
          })}
        >
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </Box>
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t(Resources.Actors.ProfilePicture.Zoom)}
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={(_e, value) => setZoom(value as number)}
            sx={{ mt: 1 }}
          />
        </Box>
      </BlModal>
    </>
  );
};
